import { YesObject } from '@yes.technology/react-toolkit'
import { isEqual, uniqBy } from 'lodash'
import { Dispatch, SetStateAction, useEffect, useRef } from 'react'
import type { FullSelectionOption } from 'site/Renderer/components/SelectAndLink/SelectAndLink.types'
import { relationObjectsAtColumn } from '../../lib/relationObjectsAtColumn'
import { relationColumnUuids } from './relationColumnUuids'

type Params = {
  currentField: string
  selectAndLinkSelectedOptions: FullSelectionOption[] | undefined
  currentRelationFromParent: YesObject[]
  unsavedObjects: YesObject[]
  setUnsavedObjects: Dispatch<SetStateAction<YesObject[]>>
  setSavedObjectsToRemove: Dispatch<SetStateAction<YesObject[]>>
  serverFieldValues: YesObject | undefined
  yesObjects: YesObject[]
  onChange: (fieldColumn: string, value: YesObject[string]) => void
  isFetching: boolean
  onScrollToRelationTable: () => void
}

const useRelationClassSelectAndLinkFieldSync = ({
  currentField,
  selectAndLinkSelectedOptions,
  currentRelationFromParent,
  unsavedObjects,
  setUnsavedObjects,
  setSavedObjectsToRemove,
  serverFieldValues,
  yesObjects,
  onChange,
  isFetching,
  onScrollToRelationTable
}: Params) => {
  const hadPersistentSelectionRef = useRef(false)
  const lastSelectAndLinkMergeUuidsRef = useRef<string[] | null>(null)

  useEffect(() => {
    lastSelectAndLinkMergeUuidsRef.current = null
  }, [currentField])

  useEffect(() => {
    if (selectAndLinkSelectedOptions === undefined) {
      lastSelectAndLinkMergeUuidsRef.current = null
      setUnsavedObjects((prev) => (prev.length ? [] : prev))
      setSavedObjectsToRemove([])
      if (hadPersistentSelectionRef.current) {
        hadPersistentSelectionRef.current = false
        const revert =
          serverFieldValues !== undefined
            ? relationObjectsAtColumn(serverFieldValues, currentField)
            : yesObjects
        onChange(currentField, uniqBy(revert, 'uuid'))
      }
      return
    }

    if (selectAndLinkSelectedOptions.length === 0) {
      setSavedObjectsToRemove([])
      const serverRelation = relationObjectsAtColumn(
        serverFieldValues,
        currentField
      )
      const serverUuidSet = new Set(serverRelation.map((o) => o.uuid))
      const nextUnsaved = currentRelationFromParent.filter(
        (o) => !serverUuidSet.has(o.uuid)
      )
      setUnsavedObjects((prev) => {
        if (
          prev.length === nextUnsaved.length &&
          prev.every((p, i) => nextUnsaved[i]?.uuid === p.uuid)
        ) {
          return prev
        }
        return nextUnsaved
      })
      return
    }

    hadPersistentSelectionRef.current = true

    const savedUuids = new Set(currentRelationFromParent.map((o) => o.uuid))

    const objectsToLink = selectAndLinkSelectedOptions.filter(
      (option) => !savedUuids.has(option.uuid)
    )

    const relationBase = uniqBy(
      [...currentRelationFromParent, ...unsavedObjects],
      'uuid'
    )

    if (objectsToLink.length || unsavedObjects.length) {
      const nextRelation = uniqBy(
        [...relationBase, ...objectsToLink],
        'uuid'
      ) as YesObject[]
      const nextUuids = relationColumnUuids(nextRelation)
      const currentUuids = relationColumnUuids(currentRelationFromParent)

      if (isEqual(nextUuids, currentUuids)) {
        lastSelectAndLinkMergeUuidsRef.current = [...nextUuids]
      } else if (!isEqual(nextUuids, lastSelectAndLinkMergeUuidsRef.current)) {
        onChange(currentField, nextRelation)
        lastSelectAndLinkMergeUuidsRef.current = [...nextUuids]
      }

      const nextUnsaved = objectsToLink as YesObject[]
      setUnsavedObjects((prev) => {
        if (
          prev.length === nextUnsaved.length &&
          prev.every((p, i) => nextUnsaved[i]?.uuid === p.uuid)
        ) {
          return prev
        }
        return nextUnsaved
      })
    }
  }, [
    currentField,
    selectAndLinkSelectedOptions,
    onChange,
    currentRelationFromParent,
    unsavedObjects,
    serverFieldValues,
    yesObjects,
    setUnsavedObjects,
    setSavedObjectsToRemove
  ])

  useEffect(() => {
    if (isFetching || !unsavedObjects.length) return

    onScrollToRelationTable()
  }, [unsavedObjects, isFetching, onScrollToRelationTable])
}

export default useRelationClassSelectAndLinkFieldSync
